import { useCallback, useRef, useState } from 'react'

/**
 * Reveal something after N rapid taps on an otherwise ordinary element — the
 * "tap the build number seven times" pattern.
 *
 * The run resets if the gap between taps exceeds `windowMs`, so ordinary
 * mis-taps by a trainee never accumulate into a reveal over a whole session.
 */
export function useSecretTap(options?: {
  taps?: number
  windowMs?: number
  onReveal?: () => void
}) {
  const taps = options?.taps ?? 5
  const windowMs = options?.windowMs ?? 1500
  const onReveal = options?.onReveal

  const count = useRef(0)
  const lastAt = useRef(0)
  const [revealed, setRevealed] = useState(false)

  const onTap = useCallback(() => {
    const now = Date.now()
    // A slow tap is the START of a new run, not a continuation of an old one.
    count.current = now - lastAt.current > windowMs ? 1 : count.current + 1
    lastAt.current = now

    if (count.current >= taps) {
      count.current = 0
      setRevealed(true)
      onReveal?.()
    }
  }, [taps, windowMs, onReveal])

  const hide = useCallback(() => {
    count.current = 0
    setRevealed(false)
  }, [])

  return { revealed, onTap, hide }
}
